import type {
  AssetValidation,
  CreateAssetValidationBody,
  UpdateAssetValidationBody,
} from "@/Features/AssetValidations/asset-validations.types";
import { monolithMoment } from "@/utils/date-format";

// Pure form types and mappers for the validation form (ENG-999). Kept free
// of React, mantine and mfui imports so the request-shaping logic is
// unit-testable (validationForm.utils.test.ts) without a component render.
// Same split as labAssetForm.utils.ts.

/**
 * The scalar form values. The documents the user drops are not part of this:
 * they never reach the POST body, each is uploaded separately as a validation
 * report once the row exists, and the create form holds them as its own
 * state. `completed_by_id` is a users.user_id; null is "not recorded". The
 * form stores the SelectBox's clear (undefined) as null so the model stays
 * number | null throughout.
 */
export interface ValidationFormData {
  validation_date: string | null;
  next_validation_due: string | null;
  // Text inputs hold "" while editing; the normalized payload holds null.
  version: string | null;
  description: string | null;
  completed_by_id: number | null;
}

// Every key seeded so mantine hands each input a defined value.
// validation_date is null (not "") because DateInput commits null on clear.
export const EMPTY_VALIDATION_FORM_DATA: ValidationFormData = {
  validation_date: null,
  next_validation_due: null,
  version: "",
  description: "",
  completed_by_id: null,
};

/**
 * Every field the form edits, derived from EMPTY_VALIDATION_FORM_DATA so a
 * field added to the form type without a seed is a compile error here, not
 * a key that silently never reaches the PATCH body. toUpdateValidationBody
 * iterates this to build its diff.
 */
export const VALIDATION_FORM_FIELDS = Object.keys(
  EMPTY_VALIDATION_FORM_DATA
) as readonly (keyof ValidationFormData)[];

/**
 * Which form fields differ from `DefaultFormData` at submit time (mantine
 * `form.getDirty()`). The edit form uses it to send only what changed.
 */
export type ValidationDirtyFields = Partial<
  Record<keyof ValidationFormData, boolean>
>;

/**
 * Version, else the date in the user's date format (what the Validation Date
 * cell shows), else a generic label — flyout title, modal subtitles.
 */
export const getValidationLabel = (
  validation: Pick<AssetValidation, "version" | "validation_date">
) =>
  validation.version ||
  monolithMoment({ timestamp: validation.validation_date ?? undefined }) ||
  "Validation";

/** "" and whitespace → null, otherwise trimmed. */
export const toNullableString = (
  value: string | null | undefined
): string | null => {
  if (typeof value !== "string") return value ?? null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

/**
 * Normalized form values → `POST /validations` body. createValidationSchema
 * is .strict() (unknown keys 400), so this is assembled key-by-key — never a
 * spread of form values. The form normalizes ("" → null, trimmed) before
 * calling this.
 */
export const toCreateValidationBody = (
  asset_uuid: string,
  data: ValidationFormData
): CreateAssetValidationBody => ({
  asset_uuid,
  validation_date: data.validation_date,
  next_validation_due: data.next_validation_due,
  version: data.version,
  description: data.description,
  completed_by_id: data.completed_by_id ?? null,
});

/**
 * AssetValidation → form values, in each input's committed shape so
 * mantine's initialValues compare clean on mount: text `null` → "",
 * `validation_date` stays the stored `YYYY-MM-DD` (DateInput commits null on
 * clear, so null stays null).
 */
export const toValidationFormData = (
  validation: AssetValidation
): ValidationFormData => ({
  validation_date: validation.validation_date ?? null,
  next_validation_due: validation.next_validation_due ?? null,
  version: validation.version ?? "",
  description: validation.description ?? "",
  completed_by_id: validation.completed_by_id ?? null,
});

/**
 * Normalized form values → `PATCH /validations/:uuid` body, dirty fields
 * only. updateValidationSchema is .strict() and merges what it receives, so
 * this is assembled key-by-key and never carries `asset_uuid` (which would
 * re-parent the validation and detach its reports). A cleared Completed By
 * reaches the API as an explicit null: `.optional()` would read an absent
 * key as "unchanged", so a clear would never land.
 */
export const toUpdateValidationBody = (
  data: ValidationFormData,
  dirtyFields: ValidationDirtyFields
): UpdateAssetValidationBody => {
  const body: UpdateAssetValidationBody = {};
  for (const key of VALIDATION_FORM_FIELDS) {
    if (!dirtyFields[key]) continue;
    if (key === "completed_by_id") {
      body.completed_by_id = data.completed_by_id ?? null;
    } else {
      body[key] = data[key];
    }
  }
  return body;
};
